import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  FileJson,
  GitBranch,
  Layers,
  Loader2,
  Palette,
  RotateCcw,
  ScrollText,
  Volume2,
} from 'lucide-react';
import { ThemePickerDialog } from '@/components/learningTutorial/ThemePickerDialog';
import { ThemeSlidePreview } from '@/components/learningTutorial/ThemeSlidePreview';
import { OrchestrationJsonDialog } from '@/components/learningTutorial/OrchestrationJsonDialog';
import { OrchestrationPassLog } from '@/components/learningTutorial/OrchestrationPassLog';
import { PipelineOutlineDialog } from '@/components/learningTutorial/PipelineOutlineDialog';
import { TutorialHistorySidebar } from '@/components/learningTutorial/TutorialHistorySidebar';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';
import api from '@/lib/api';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { EMPTY_OPTIONS, fetchLearningTutorialOptions } from '@/lib/learningTutorial/catalog';
import { isProgrammingTopic } from '@/lib/learningTutorial/programmingContext';
import { buildOrchestrationViewerTabs } from '@/lib/learningTutorial/orchestrationViewerTabs';
import {
  TUTORIAL_PIPELINE_STEPS,
  buildExtendedPassLog,
  pipelineProgressPct,
  pipelineStepStatus,
} from '@/lib/learningTutorial/pipelineSteps';
import {
  TUTORIAL_RUN_STEPS,
  canResumePipeline,
  pipelineStatusAfterStep,
  resolveResumeStep,
  resumeStepMeta,
  runTutorialStep,
  stepsFrom,
} from '@/lib/learningTutorial/pipelineRunner';
import { MurfVoiceFields } from '@/components/tts/MurfVoiceFields';
import { languageToMurfLocale } from '@/lib/murfVoices';
import {
  hydrateTutorialFormFromProject,
  readTutorialHistorySidebarOpen,
  tutorialUiStateFromProject,
  writeTutorialHistorySidebarOpen,
} from '@/lib/learningTutorial/tutorialHistory';
import { ArtifactStrategyPicker } from '@/components/learningTutorial/ArtifactStrategyPicker';
import { flowHistoryPanelPaddingClass } from '@/components/creationFlows/FlowHistoryPanel';

import { TwinneeLogo } from '@/components/TwinneeLogo';
export const TutorialFlowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [objective, setObjective] = useState('');
  const [audience, setAudience] = useState('beginners');
  const [duration, setDuration] = useState(8);
  const [prerequisites, setPrerequisites] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [includeCode, setIncludeCode] = useState(false);
  const [visualNotes, setVisualNotes] = useState(
    'Beginner-first: plain language, diagrams, flowcharts, and real-world analogies per concept.',
  );

  const [language, setLanguage] = useState('en');
  const [themeId, setThemeId] = useState('slate_emerald');
  const [layoutMode, setLayoutMode] = useState('per_scene');
  const [ttsProvider, setTtsProvider] = useState('edge');
  const [ttsVoiceStyle, setTtsVoiceStyle] = useState('storyteller');
  const [ttsVoiceId, setTtsVoiceId] = useState('');
  const [ttsLocale, setTtsLocale] = useState('en-US');
  const [visualProvider, setVisualProvider] = useState('gemini');
  const [generateVisuals, setGenerateVisuals] = useState(true);
  const [artifactStrategy, setArtifactStrategy] = useState('auto');
  const [preferredArtifacts, setPreferredArtifacts] = useState([
    'Explanation',
    'Diagram',
    'Real World Analogy',
    'Example',
  ]);
  const [minArtifactsPerTopic, setMinArtifactsPerTopic] = useState(3);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);

  const [projectId, setProjectId] = useState(null);
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [orchestration, setOrchestration] = useState(null);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerTabId, setViewerTabId] = useState('pass_log');
  const [historyOpen, setHistoryOpen] = useState(readTutorialHistorySidebarOpen);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [pipelineError, setPipelineError] = useState(null);

  useEffect(() => {
    const nav = getIntakeNavPrefill(location.state);
    if (!nav) return;
    if (nav.title) setTitle(nav.title);
    if (nav.tutorialTopic) setTopic(nav.tutorialTopic);
    if (nav.language) setLanguage(nav.language);
  }, [location.state]);

  const toggleHistory = useCallback(() => {
    setHistoryOpen((prev) => {
      const next = !prev;
      writeTutorialHistorySidebarOpen(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (isProgrammingTopic(topic, objective, title)) {
      setIncludeCode(true);
    }
  }, [topic, objective, title]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalog = await fetchLearningTutorialOptions(api);
        if (cancelled) return;
        setOptions(catalog);
        const d = catalog.defaults || {};
        if (d.theme_id) setThemeId(d.theme_id);
        if (d.layout_mode) setLayoutMode(d.layout_mode);
        if (d.language) setLanguage(d.language);
        if (d.tts_provider) setTtsProvider(d.tts_provider);
        if (d.tts_voice_style) setTtsVoiceStyle(d.tts_voice_style);
        if (d.visual_provider) setVisualProvider(d.visual_provider);
        if (typeof d.generate_visuals === 'boolean') setGenerateVisuals(d.generate_visuals);
        if (d.artifact_strategy) setArtifactStrategy(d.artifact_strategy);
        if (Array.isArray(d.preferred_artifacts) && d.preferred_artifacts.length) {
          setPreferredArtifacts(d.preferred_artifacts);
        }
        if (d.min_artifacts_per_topic) setMinArtifactsPerTopic(d.min_artifacts_per_topic);
      } catch {
        if (!cancelled) toast.error('Could not load tutorial options');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedTheme = useMemo(
    () => (options.themes || []).find((t) => t.id === themeId),
    [options.themes, themeId],
  );

  const ttsProviderMeta = useMemo(
    () => (options.tts_providers || []).find((p) => p.id === ttsProvider),
    [options.tts_providers],
  );

  const elevenlabsVoices = options.elevenlabs_voices || [];
  const murfVoices = options.murf_voices || [];
  const murfLocales = options.murf_locales || [];

  const handleTtsProviderChange = (provider) => {
    setTtsProvider(provider);
    setTtsVoiceId('');
    if (provider === 'murf') {
      setTtsVoiceStyle('Promo');
      setTtsLocale(languageToMurfLocale(language));
    } else if (provider === 'edge') {
      setTtsVoiceStyle('storyteller');
    }
  };

  const progressPct = useMemo(
    () => pipelineProgressPct(currentStep, status),
    [currentStep, status],
  );

  const extendedPassLog = useMemo(
    () => buildExtendedPassLog(orchestration, project),
    [orchestration, project],
  );

  const intakePayload = () => ({
    topic: topic.trim(),
    learning_objective: objective.trim() || topic.trim(),
    audience,
    duration_minutes: Number(duration) || 8,
    language,
    prerequisites: prerequisites.trim(),
    key_points: keyPoints.split('\n').map((s) => s.trim()).filter(Boolean),
    include_code: includeCode,
    visual_style_notes: visualNotes.trim(),
    reference_image_urls: [],
    theme_id: themeId,
    layout_mode: layoutMode,
    tts_provider: ttsProvider,
    tts_voice_id: ttsVoiceId || null,
    tts_voice_style: ttsVoiceStyle,
    tts_locale: ttsProvider === 'murf' ? (ttsLocale || languageToMurfLocale(language)) : null,
    visual_provider: generateVisuals ? visualProvider : 'none',
    generate_visuals: generateVisuals,
    artifact_strategy: artifactStrategy,
    preferred_artifacts: artifactStrategy === 'guided' ? preferredArtifacts : [],
    min_artifacts_per_topic: minArtifactsPerTopic,
  });

  const viewerTabs = useMemo(
    () => buildOrchestrationViewerTabs(orchestration),
    [orchestration],
  );

  const fetchProject = useCallback(async (pid) => {
    const { data } = await api.get(`/learning-tutorials/projects/${pid}`);
    setProject(data.project);
    return data.project;
  }, []);

  const advanceStep = (stepId) => {
    setCurrentStep(stepId);
    setOutlineOpen(true);
  };

  const runSteps = useCallback(async (pid, stepIds) => {
    let lastOrch = null;
    let lastProject = null;
    for (const stepId of stepIds) {
      advanceStep(stepId);
      if (stepId === 'pass2') {
        toast.message('Pass 2 — expanding artifacts (LLM). Large topics may take several minutes…');
      } else if (stepId === 'pass2_optimize') {
        toast.message('Pass 2 optimize — deduplicating topics and filling gaps…');
      } else if (stepId === 'script') {
        toast.message('Pass 3 — writing trainer script (LLM)…');
      } else if (stepId === 'narration_audio') {
        toast.message('Generating trainer narration audio (TTS)…');
      } else if (stepId === 'word_align') {
        toast.message('Running WhisperX word alignment…');
      } else if (stepId === 'artifact_embed') {
        toast.message('Embedding artifact render times from trainer script…');
      }
      const result = await runTutorialStep(api, pid, stepId);
      if (result.orchestration) {
        lastOrch = result.orchestration;
        setOrchestration(result.orchestration);
      }
      if (result.project) {
        lastProject = result.project;
        setProject(result.project);
      } else if (
        stepId === 'timeline'
        || stepId === 'script'
        || stepId === 'narration_audio'
        || stepId === 'word_align'
        || stepId === 'artifact_embed'
      ) {
        lastProject = await fetchProject(pid);
      }
      if (result.toastMessage) {
        toast.success(result.toastMessage);
      }
    }
    return {
      orchestration: lastOrch,
      project: lastProject || (await fetchProject(pid)),
    };
  }, [fetchProject]);

  const finishPipelineSuccess = useCallback(() => {
    advanceStep('done');
    setStatus('ready');
    setPipelineError(null);
    setHistoryRefreshKey((k) => k + 1);
    toast.success('Pipeline complete — review outline or open editor');
  }, []);

  const executeResume = useCallback(async (stepId, { continueAfter = false } = {}) => {
    if (!projectId || !stepId) return;
    if (status === 'working') return;

    setPipelineError(null);
    setStatus('working');
    setOutlineOpen(true);
    setCurrentStep(stepId);

    const stepIds = continueAfter ? stepsFrom(stepId) : [stepId];
    try {
      const { orchestration: orch, project: proj } = await runSteps(projectId, stepIds);
      const lastStep = stepIds[stepIds.length - 1];
      const finalOrch = orch || proj?.orchestration || orchestration;
      if (finalOrch && !orch) setOrchestration(finalOrch);

      if (continueAfter && lastStep === 'artifact_embed') {
        finishPipelineSuccess();
        return;
      }

      const next = pipelineStatusAfterStep(lastStep, proj, finalOrch);
      setCurrentStep(next.currentStep);
      setStatus(next.status);
      if (next.status === 'ready') {
        setHistoryRefreshKey((k) => k + 1);
        toast.success('Pipeline complete — review outline or open editor');
      } else {
        toast.success(`${resumeStepMeta(stepId).shortLabel} step completed`);
      }
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(String(err.message || ''));
      let message = err.response?.data?.detail || err.message || 'Tutorial pipeline failed';
      if (isTimeout && (stepId === 'narration_audio' || stepId === 'word_align' || stepId === 'artifact_embed')) {
        message = 'Narration step timed out in the browser — the server may still be working. Wait a minute, then retry this step.';
      } else if (isTimeout && (stepId === 'pass1' || stepId === 'pass2' || stepId === 'pass2_optimize' || stepId === 'script')) {
        message = `${resumeStepMeta(stepId).shortLabel} timed out in the browser — the server may still be working. Wait a minute, then retry this step.`;
      }
      setStatus('error');
      setPipelineError(message);
      setCurrentStep(stepId);
      toast.error(message);
    }
  }, [projectId, status, runSteps, orchestration, finishPipelineSuccess]);

  const retryStep = useCallback(() => {
    const stepId = resolveResumeStep({ project, currentStepId: currentStep, pipelineStatus: status });
    if (stepId) executeResume(stepId, { continueAfter: false });
  }, [project, currentStep, status, executeResume]);

  const retryAndContinue = useCallback(() => {
    const stepId = resolveResumeStep({ project, currentStepId: currentStep, pipelineStatus: status });
    if (stepId) executeResume(stepId, { continueAfter: true });
  }, [project, currentStep, status, executeResume]);

  const runPipelineStep = useCallback(
    (stepId) => executeResume(stepId, { continueAfter: false }),
    [executeResume],
  );

  const createAndRun = async () => {
    if (!title.trim() || topic.trim().length < 3) {
      toast.error('Title and topic (3+ chars) are required');
      return;
    }
    if (ttsProvider === 'murf' && !ttsVoiceId) {
      toast.error('Select a Murf voice');
      return;
    }
    if (ttsProviderMeta && !ttsProviderMeta.configured && ttsProvider !== 'edge') {
      toast.error(`${ttsProviderMeta.label} is not configured on the server`);
      return;
    }
    if (artifactStrategy === 'guided' && preferredArtifacts.length < 3) {
      toast.error('Guided mode: select at least 3 artifact types');
      return;
    }

    setPipelineError(null);
    setStatus('working');
    setOrchestration(null);
    setProject(null);
    setOutlineOpen(true);

    try {
      const { data: created } = await api.post('/learning-tutorials/projects', {
        title: title.trim(),
        intake: intakePayload(),
        pipeline_mode: 'tutorial_staged',
        story_type: 'tutorial_flow',
      });
      const pid = created.project.id;
      setProjectId(pid);
      setProject(created.project);

      await runSteps(pid, TUTORIAL_RUN_STEPS);
      finishPipelineSuccess();
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Tutorial pipeline failed';
      setStatus('error');
      setPipelineError(message);
      toast.error(message);
    }
  };

  const openOutlineForPass = (snapshotKey) => {
    const stepMap = {
      pass1: 'pass1',
      pass2: 'pass2',
      pass2_optimize: 'pass2_optimize',
      pass3: 'script',
      timeline: 'timeline',
      narration_audio: 'narration_audio',
      word_align: 'word_align',
      artifact_embed: 'artifact_embed',
    };
    setOutlineOpen(true);
    if (stepMap[snapshotKey]) {
      setCurrentStep(stepMap[snapshotKey]);
    }
  };

  const resetNewTutorial = () => {
    setProjectId(null);
    setProject(null);
    setOrchestration(null);
    setStatus('idle');
    setCurrentStep(null);
    setPipelineError(null);
    setOutlineOpen(false);
    setTitle('');
    setTopic('');
    setObjective('');
    setAudience('beginners');
    setDuration(8);
    setPrerequisites('');
    setKeyPoints('');
    setIncludeCode(true);
    setVisualNotes(
      'Beginner-first: plain language, diagrams, flowcharts, and real-world analogies per concept.',
    );
    const d = options.defaults || {};
    if (d.theme_id) setThemeId(d.theme_id);
    if (d.layout_mode) setLayoutMode(d.layout_mode);
    if (d.language) setLanguage(d.language);
    if (d.tts_provider) setTtsProvider(d.tts_provider);
    if (d.tts_voice_style) setTtsVoiceStyle(d.tts_voice_style);
    setTtsVoiceId('');
    if (d.visual_provider) setVisualProvider(d.visual_provider);
    if (typeof d.generate_visuals === 'boolean') setGenerateVisuals(d.generate_visuals);
    if (d.artifact_strategy) setArtifactStrategy(d.artifact_strategy);
    if (Array.isArray(d.preferred_artifacts)) setPreferredArtifacts(d.preferred_artifacts);
    if (d.min_artifacts_per_topic) setMinArtifactsPerTopic(d.min_artifacts_per_topic);
  };

  const loadHistoricalProject = async (summary) => {
    if (!summary?.id || summary.id === projectId) return;
    if (status === 'working') {
      toast.error('Wait for the current pipeline to finish');
      return;
    }
    try {
      const full = await fetchProject(summary.id);
      hydrateTutorialFormFromProject(full, {
        setTitle,
        setTopic,
        setObjective,
        setAudience,
        setDuration,
        setPrerequisites,
        setKeyPoints,
        setIncludeCode,
        setVisualNotes,
        setLanguage,
        setThemeId,
        setLayoutMode,
        setTtsProvider,
        setTtsVoiceStyle,
        setTtsVoiceId,
        setTtsLocale,
        setVisualProvider,
        setGenerateVisuals,
        setArtifactStrategy,
        setPreferredArtifacts,
        setMinArtifactsPerTopic,
      });
      setProjectId(full.id);
      setOrchestration(full.orchestration || null);
      const ui = tutorialUiStateFromProject(full);
      setCurrentStep(ui.currentStep);
      setStatus(ui.status);
      setPipelineError(
        ui.status === 'error' ? (full.last_pipeline_error || null) : null,
      );
      setOutlineOpen(false);
      toast.success('Loaded tutorial from history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load tutorial');
    }
  };

  const showPipelinePanel = status !== 'idle' || Boolean(orchestration || project?.timeline);

  const resumeStepId = useMemo(
    () => resolveResumeStep({ project, currentStepId: currentStep, pipelineStatus: status }),
    [project, currentStep, status],
  );

  const resumeMeta = useMemo(
    () => (resumeStepId ? resumeStepMeta(resumeStepId) : null),
    [resumeStepId],
  );

  const showResumeActions = useMemo(
    () => canResumePipeline({
      projectId,
      project,
      pipelineStatus: status,
      currentStepId: currentStep,
      orchestration,
    }),
    [projectId, project, status, currentStep, orchestration],
  );

  const stepStatusCtx = useMemo(
    () => ({ currentStepId: currentStep, pipelineStatus: status, project, orchestration }),
    [currentStep, status, project, orchestration],
  );

  return (
    <div
      className={`relative min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 transition-[padding] duration-200 ease-out ${flowHistoryPanelPaddingClass(historyOpen)}`}
    >
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <TwinneeWordmark className="h-8" />
        <span className="text-sm text-slate-400 flex items-center gap-2">
          <Layers className="w-4 h-4 text-violet-400" />
          Tutorial Flow
        </span>
      </header>

      <main className="max-w-3xl w-full mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold mb-2">Tutorial Flow — staged generation</h1>
          <p className="text-slate-400 mb-6">
            Multi-pass pipeline with live outline updates after each stage — concept tree, artifacts,
            script, timeline blocks, and narration sync.
          </p>

          {showPipelinePanel && (
            <div className="mb-6 rounded-xl border border-amber-500/25 bg-black/40 p-4">
              <div className="flex items-center justify-between mb-2 gap-3">
                <span className="text-sm text-amber-200/90">Pipeline progress</span>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <span className="text-sm text-slate-400 tabular-nums">{progressPct}%</span>
                  {showResumeActions && resumeMeta ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                        disabled={status === 'working'}
                        onClick={retryStep}
                        data-testid="tutorial-retry-step"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Retry {resumeMeta.shortLabel}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        disabled={status === 'working'}
                        onClick={retryAndContinue}
                        data-testid="tutorial-retry-continue"
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Retry &amp; continue
                      </Button>
                    </>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-sm border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                    onClick={() => setOutlineOpen(true)}
                  >
                    <ScrollText className="w-4 h-4 mr-1.5" />
                    Step details
                  </Button>
                </div>
              </div>
              <Progress value={progressPct} className="h-2" />

              {pipelineError && status === 'error' ? (
                <p className="mt-2 text-xs text-red-300/90 rounded-sm border border-red-500/25 bg-red-500/10 px-2 py-1.5">
                  {pipelineError}
                </p>
              ) : null}

              <ul className="mt-4 space-y-1.5 text-sm">
                {TUTORIAL_PIPELINE_STEPS.map((step) => {
                  const st = pipelineStepStatus(step.id, stepStatusCtx);
                  return (
                    <li key={step.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (st !== 'pending') {
                            setCurrentStep(step.id);
                            setOutlineOpen(true);
                          }
                        }}
                        className="flex items-center gap-2 text-left w-full rounded-sm px-1 py-0.5 hover:bg-white/5 disabled:opacity-50"
                        disabled={st === 'pending'}
                      >
                        {st === 'done' ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                        ) : st === 'active' ? (
                          <Loader2 className="w-4 h-4 animate-spin text-amber-400 shrink-0" />
                        ) : st === 'error' ? (
                          <GitBranch className="w-4 h-4 text-red-400 shrink-0" />
                        ) : (
                          <GitBranch className="w-4 h-4 text-slate-600 shrink-0" />
                        )}
                        <span
                          className={
                            st === 'active'
                              ? 'text-amber-200'
                              : st === 'done'
                                ? 'text-slate-400'
                                : 'text-slate-500'
                          }
                        >
                          {step.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {extendedPassLog.length > 0 ? (
                <div className="mt-4 pt-4 border-t border-amber-500/15">
                  <OrchestrationPassLog
                    entries={extendedPassLog}
                    onViewOutput={(key) => openOutlineForPass(key)}
                  />
                </div>
              ) : null}

              {viewerTabs.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-sm border-slate-600 text-slate-300"
                  onClick={() => {
                    setViewerTabId(orchestration?.pass_log?.length ? 'pass_log' : viewerTabs[0]?.id);
                    setViewerOpen(true);
                  }}
                >
                  <FileJson className="w-4 h-4 mr-2" />
                  View full JSON
                </Button>
              )}

              {status === 'ready' && projectId ? (
                <Button
                  type="button"
                  size="sm"
                  className="mt-4 ml-2 rounded-sm bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                  onClick={() => navigate(`/learning/${projectId}/editor`)}
                >
                  Open editor
                </Button>
              ) : null}
            </div>
          )}

          <div className="space-y-4 rounded-2xl border border-slate-700 bg-slate-900/60 p-6">
            <div>
              <Label>Lesson title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 bg-slate-800" />
            </div>
            <div>
              <Label>Topic</Label>
              <Input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. Palindrome Program"
                className="mt-1 bg-slate-800"
              />
            </div>
            <div>
              <Label>Learning objective</Label>
              <Textarea value={objective} onChange={(e) => setObjective(e.target.value)} className="mt-1 bg-slate-800" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Audience</Label>
                <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-1 bg-slate-800" />
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input type="number" min={1} max={60} value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-1 bg-slate-800" />
              </div>
            </div>
            <div>
              <Label>Key points (optional, one per line)</Label>
              <Textarea value={keyPoints} onChange={(e) => setKeyPoints(e.target.value)} className="mt-1 bg-slate-800" rows={3} />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={includeCode}
                onChange={(e) => setIncludeCode(e.target.checked)}
              />
              <span>
                Include code artifacts when the topic is technical
                {isProgrammingTopic(topic, objective, title) ? (
                  <span className="block text-xs text-amber-400/90 mt-0.5">
                    Suggested for programming topics — Pass 2 chooses walkthroughs and dry runs (optional, not guaranteed).
                  </span>
                ) : null}
              </span>
            </label>

            <ArtifactStrategyPicker
              strategy={artifactStrategy}
              onStrategyChange={setArtifactStrategy}
              preferredArtifacts={preferredArtifacts}
              onPreferredChange={setPreferredArtifacts}
              minArtifacts={minArtifactsPerTopic}
              onMinArtifactsChange={setMinArtifactsPerTopic}
              artifactGroups={options.artifact_groups || []}
              disabled={optionsLoading || status === 'working'}
            />
          </div>

          <div className="mt-6 space-y-4 rounded-2xl border border-slate-700 bg-slate-900/40 p-6">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Palette className="w-5 h-5 text-violet-400" />
              Slides & voice
            </h2>
            <div className="flex gap-3 items-start">
              <ThemeSlidePreview theme={selectedTheme} size="large" />
              <Button
                type="button"
                variant="outline"
                className="border-slate-600"
                onClick={() => setThemeDialogOpen(true)}
                disabled={optionsLoading}
              >
                Browse themes
              </Button>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage} disabled={optionsLoading}>
                <SelectTrigger className="mt-1 bg-slate-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(options.languages || []).map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>{lang.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 space-y-4 rounded-2xl border border-slate-700 bg-slate-900/40 p-6">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Volume2 className="w-5 h-5 text-sky-400" />
              Narration voice
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>TTS provider</Label>
                <Select value={ttsProvider} onValueChange={handleTtsProviderChange} disabled={optionsLoading}>
                  <SelectTrigger className="mt-1 bg-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(options.tts_providers || []).map((p) => (
                      <SelectItem key={p.id} value={p.id} disabled={!p.configured}>
                        {p.label}
                        {!p.configured ? ' (not configured)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {ttsProvider === 'edge' && (
                <div>
                  <Label>Edge voice style</Label>
                  <Select value={ttsVoiceStyle} onValueChange={setTtsVoiceStyle}>
                    <SelectTrigger className="mt-1 bg-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(options.edge_voice_styles || []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {ttsProvider === 'elevenlabs' && (
                <div>
                  <Label>ElevenLabs voice</Label>
                  <Select value={ttsVoiceId || '_none'} onValueChange={(v) => setTtsVoiceId(v === '_none' ? '' : v)}>
                    <SelectTrigger className="mt-1 bg-slate-800">
                      <SelectValue placeholder="Choose voice" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Default voice</SelectItem>
                      {elevenlabsVoices.map((v) => (
                        <SelectItem key={v.voice_id || v.id} value={v.voice_id || v.id}>
                          {v.name || v.label || v.voice_id}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {ttsProvider === 'murf' && (
                <MurfVoiceFields
                  language={language}
                  voices={murfVoices}
                  apiLocales={murfLocales}
                  voiceId={ttsVoiceId}
                  voiceStyle={ttsVoiceStyle}
                  onVoiceIdChange={setTtsVoiceId}
                  onVoiceStyleChange={setTtsVoiceStyle}
                  onLocaleChange={setTtsLocale}
                  voiceSelectTestId="tutorial-murf-voice-select"
                  styleSelectTestId="tutorial-murf-style-select"
                  localeSelectTestId="tutorial-murf-locale-select"
                />
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-sm"
              disabled={status === 'working' || optionsLoading}
              onClick={createAndRun}
              data-testid="tutorial-generate-btn"
            >
              {status === 'working' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Running staged pipeline…
                </>
              ) : (
                <>
                  <TwinneeLogo className="w-5 h-5 mr-2" />
                  {showResumeActions && projectId ? 'Start new tutorial' : 'Generate tutorial video'}
                </>
              )}
            </Button>
            {(orchestration || project?.timeline) && (
              <Button
                type="button"
                variant="outline"
                className="rounded-sm border-slate-600"
                onClick={() => setOutlineOpen(true)}
              >
                <ScrollText className="w-4 h-4 mr-2" />
                Pipeline outline
              </Button>
            )}
          </div>
        </motion.div>
      </main>

      <TutorialHistorySidebar
        open={historyOpen}
        onToggle={toggleHistory}
        activeProjectId={projectId}
        onSelectProject={loadHistoricalProject}
        onNewTutorial={resetNewTutorial}
        refreshKey={historyRefreshKey}
      />

      <ThemePickerDialog
        open={themeDialogOpen}
        onOpenChange={setThemeDialogOpen}
        themes={options.themes || []}
        selectedThemeId={themeId}
        onSelectTheme={setThemeId}
      />

      <PipelineOutlineDialog
        open={outlineOpen}
        onOpenChange={setOutlineOpen}
        currentStepId={currentStep}
        pipelineStatus={status}
        orchestration={orchestration}
        project={project}
        initialStepId={currentStep}
        resumeStepId={resumeStepId}
        pipelineError={pipelineError}
        resumeLoading={status === 'working'}
        onRetryStep={showResumeActions ? retryStep : undefined}
        onRetryAndContinue={showResumeActions ? retryAndContinue : undefined}
        onRunStep={runPipelineStep}
        onOpenEditor={
          status === 'ready' && projectId
            ? () => navigate(`/learning/${projectId}/editor`)
            : undefined
        }
      />

      <OrchestrationJsonDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        title="Tutorial orchestration output"
        description="Pass log records each output; open any pass tab to inspect tree or JSON."
        orchestration={orchestration}
        initialTabId={viewerTabId}
        tabs={viewerTabs}
      />
    </div>
  );
};
